import { useEffect, useState, useRef } from "react";
import { toast } from "react-toastify";
import { Watch } from "react-loader-spinner";
import Button from 'react-bootstrap/Button';
import ButtonGroup from 'react-bootstrap/ButtonGroup';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import Select from "react-select";
import axios from "axios";
import { API_URL } from "../../shared/constant";

const TileVisualizer = () => {
  const canvasRef = useRef(null);
  const roomImageRef = useRef(null);
  
  const [roomImage, setRoomImage] = useState(null);
  const [tile, setTile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detectedAreas, setDetectedAreas] = useState([]);
  const [tileScale, setTileScale] = useState(1);
  const [tileOrientation, setTileOrientation] = useState('horizontal'); // 'horizontal' or 'vertical'
  const [flipHorizontal, setFlipHorizontal] = useState(false);
  const [flipVertical, setFlipVertical] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [applying, setApplying] = useState(false);
  const [productDrpData, setProductDrpData] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [groutWidth, setGroutWidth] = useState(0); // Grout width in pixels
  const [groutColor, setGroutColor] = useState('#000000'); // Default white grout
  const [tileLayout, setTileLayout] = useState('straight'); // Tile layout pattern
  const [floorView, setFloorView] = useState(false); // Floor perspective view
  const hasAutoDetectedRef = useRef(false);

  // Initialize canvas
  useEffect(() => {
    if (canvasRef.current && roomImage) {
      setApplying(true);
      // Use setTimeout to allow UI to update before heavy rendering
      setTimeout(() => {
        drawCanvas();
        setApplying(false);
      }, 10);
    }
  }, [roomImage, tile, detectedAreas, tileScale, tileOrientation, flipHorizontal, flipVertical, groutWidth, groutColor, tileLayout, floorView]);

  // Fetch products on component mount
  useEffect(() => {
    getProductData();
  }, []);

  // Auto-detect tiles when both images are uploaded
  useEffect(() => {
    if (roomImage && tile && !hasAutoDetectedRef.current && !detecting) {
      hasAutoDetectedRef.current = true;
      detectTiles().catch(error => {
        console.error('Auto-detection error:', error);
        hasAutoDetectedRef.current = false; // Reset on error so it can retry
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomImage, tile]);

  const drawCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw room image
    if (roomImage) {
      const img = new Image();
      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;
        
        // First, draw the room image (preserves original image and mask)
        ctx.drawImage(img, 0, 0);
        
        // If no transparent regions detected, keep the image exactly as is
        if (detectedAreas.length === 0) {
          setApplying(false);
          return; // Preserve original image without any modifications
        }
        
        // Get the original image data to use as a mask for transparent areas
        const originalImageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        
        // Track how many areas we need to draw
        let areasToDraw = detectedAreas.filter(area => tile).length;
        let areasDrawn = 0;
        
        // Draw all detected areas with tiles (only on transparent areas)
        detectedAreas.forEach(area => {
          if (tile) {
            drawTiledAreaOnTransparent(ctx, area, tile, originalImageData, () => {
              areasDrawn++;
              if (areasDrawn >= areasToDraw) {
                setApplying(false);
              }
            });
          } else {
            // Draw selection outline
            drawSelectionArea(ctx, area, 'rgba(0, 150, 255, 0.3)');
            if (areasToDraw === 0) {
              setApplying(false);
            }
          }
        });
        
        // If no tiles to draw, set applying to false immediately
        if (areasToDraw === 0) {
          setApplying(false);
        }
      };
      img.src = roomImage;
    }
  };

  // Helper function to get layout label
  const getLayoutLabel = (layout) => {
    const labels = {
      'straight': 'Straight Stack/Grid',
      'running-bond': 'Running Bond (Brick Pattern)',
      'diamond': 'Diamond',
      'basketweave': 'Basketweave',
    };
    return labels[layout] || 'Straight Stack/Grid';
  };

  // Create pattern canvas based on layout type
  const createPatternCanvas = (img, tileWidth, tileHeight, patternWidth, patternHeight, layout, orientationRotation) => {
    const patternCanvas = document.createElement('canvas');
    const patternCtx = patternCanvas.getContext('2d');
    
    // Set canvas size based on layout
    let canvasWidth = patternWidth;
    let canvasHeight = patternHeight;
    
    if (layout === 'herringbone' || layout === 'chevron') {
      // Herringbone and Chevron need more space for diagonal patterns
      canvasWidth = patternWidth * 2;
      canvasHeight = patternHeight * 2;
    } else if (layout === 'basketweave') {
      // Basketweave needs space for pairs
      canvasWidth = patternWidth * 2;
      canvasHeight = patternHeight * 2;
    } else if (layout === 'running-bond' || layout === 'offset-1/3' || layout === 'offset-1/4') {
      // Offset patterns need space for two rows
      canvasWidth = patternWidth * 2;
      canvasHeight = patternHeight * 2;
    }
    
    patternCanvas.width = canvasWidth;
    patternCanvas.height = canvasHeight;
    
    // Fill with grout color
    patternCtx.fillStyle = groutColor;
    patternCtx.fillRect(0, 0, canvasWidth, canvasHeight);
    
    // Draw tiles based on layout
    patternCtx.save();
    
    const drawTile = (x, y, additionalRotation = 0, sizeScale = 1) => {
      patternCtx.save();
      patternCtx.translate(x, y);
      
      // Apply orientation rotation (vertical = 90 degrees)
      const rad = (orientationRotation * Math.PI) / 180;
      if (orientationRotation !== 0) {
        patternCtx.rotate(rad);
      }
      
      // Apply additional rotation for patterns like herringbone, chevron, diamond
      if (additionalRotation !== 0) {
        patternCtx.rotate(additionalRotation);
      }
      
      // Apply flip transformations and size scale
      const scaleX = tileScale * sizeScale * (flipHorizontal ? -1 : 1);
      const scaleY = tileScale * sizeScale * (flipVertical ? -1 : 1);
      patternCtx.scale(scaleX, scaleY);
      
      patternCtx.drawImage(img, -img.width / 2, -img.height / 2);
      patternCtx.restore();
    };
    
    const baseTileWidth = patternWidth - groutWidth;
    const baseTileHeight = patternHeight - groutWidth;
    
    switch (layout) {
      case 'straight':
        // Simple grid - draw one tile
        drawTile(patternWidth / 2, patternHeight / 2);
        break;
        
      case 'running-bond':
        // Brick pattern - offset every other row by half
        drawTile(patternWidth / 2, patternHeight / 2);
        drawTile(patternWidth + patternWidth / 2, patternHeight / 2 + patternHeight);
        break;
        
      case 'diamond':
        // Diamond - tiles rotated 45 degrees, scaled smaller (70% size)
        const diamondAngle = Math.PI / 4;
        const diamondSizeScale = 1.2; // Make diamonds 70% of original size
        drawTile(patternWidth / 2, patternHeight / 2, diamondAngle, diamondSizeScale);
        break;
        
      case 'basketweave':
        // Basketweave - interlocking pairs (horizontal and vertical)
        const bwSize = Math.max(baseTileWidth, baseTileHeight) + groutWidth;
        // Horizontal pair (top row)
        drawTile(bwSize / 2, bwSize / 2);
        drawTile(bwSize * 1.5, bwSize / 2);
        // Vertical tile below (centered)
        drawTile(bwSize, bwSize * 1.5);
        // Next horizontal pair (to the right)
        drawTile(bwSize * 2.5, bwSize / 2);
        drawTile(bwSize * 3.5, bwSize / 2);
        // Vertical tile below that
        drawTile(bwSize * 3, bwSize * 1.5);
        break;
        
      default:
        drawTile(patternWidth / 2, patternHeight / 2);
    }
    
    patternCtx.restore();
    return patternCanvas;
  };

  // Draw tiles only on transparent areas using the original image as a mask
  const drawTiledAreaOnTransparent = (ctx, area, tileImage, originalImageData, onComplete) => {
    const img = new Image();
    img.onload = () => {
      // Calculate scaled tile dimensions
      const scaledWidth = img.width * tileScale;
      const scaledHeight = img.height * tileScale;
      
      // Calculate dimensions needed for rotation
      // Add 90 degrees if vertical orientation is selected
      const orientationRotation = tileOrientation === 'vertical' ? 90 : 0;
      const rad = (orientationRotation * Math.PI) / 180;
      const cos = Math.abs(Math.cos(rad));
      const sin = Math.abs(Math.sin(rad));
      const tileWidth = scaledWidth;
      const tileHeight = scaledHeight;
      const basePatternWidth = tileWidth * cos + tileHeight * sin;
      const basePatternHeight = tileWidth * sin + tileHeight * cos;
      
      // Add grout spacing to pattern dimensions (grout appears on right and bottom)
      const patternWidth = basePatternWidth + groutWidth;
      const patternHeight = basePatternHeight + groutWidth;
      
      // Create pattern canvas based on selected layout
      const tileCanvas = createPatternCanvas(img, tileWidth, tileHeight, patternWidth, patternHeight, tileLayout, orientationRotation);
      
      // Create pattern from the processed tile with grout
      const pattern = ctx.createPattern(tileCanvas, 'repeat');
      
      // Save context
      ctx.save();
      
      // Create a temporary canvas to draw tiles on
      const tilesLayer = document.createElement('canvas');
      const tilesLayerCtx = tilesLayer.getContext('2d');
      tilesLayer.width = ctx.canvas.width;
      tilesLayer.height = ctx.canvas.height;
      
      // Clip to selection area and fill with pattern on the tiles layer
      tilesLayerCtx.beginPath();
      if (area.type === 'rect') {
        tilesLayerCtx.rect(area.x, area.y, area.width, area.height);
      } else if (area.type === 'polygon') {
        tilesLayerCtx.moveTo(area.points[0].x, area.points[0].y);
        for (let i = 1; i < area.points.length; i++) {
          tilesLayerCtx.lineTo(area.points[i].x, area.points[i].y);
        }
        tilesLayerCtx.closePath();
      }
      tilesLayerCtx.clip();
      
      // Fill with pattern (this fills the entire clipped area)
      tilesLayerCtx.fillStyle = pattern;
      if (area.type === 'rect') {
        tilesLayerCtx.fillRect(area.x, area.y, area.width, area.height);
      } else {
        tilesLayerCtx.beginPath();
        tilesLayerCtx.moveTo(area.points[0].x, area.points[0].y);
        for (let i = 1; i < area.points.length; i++) {
          tilesLayerCtx.lineTo(area.points[i].x, area.points[i].y);
        }
        tilesLayerCtx.closePath();
        tilesLayerCtx.fill();
      }
      
      // Create a mask that only includes transparent pixels from the original image
      const maskCanvas = document.createElement('canvas');
      const maskCtx = maskCanvas.getContext('2d');
      maskCanvas.width = ctx.canvas.width;
      maskCanvas.height = ctx.canvas.height;
      
      // Create mask data - only transparent pixels are visible
      const maskData = maskCtx.createImageData(maskCanvas.width, maskCanvas.height);
      // Use a stricter threshold - only truly transparent or very semi-transparent pixels
      // Lower threshold means only pixels with very low alpha (< 128) are considered transparent
      const alphaThreshold = 200;
      
      // Initialize all mask pixels to transparent (black with 0 alpha)
      // This ensures no tiles show through by default
      for (let i = 0; i < maskData.data.length; i += 4) {
        maskData.data[i] = 0;     // R
        maskData.data[i + 1] = 0; // G
        maskData.data[i + 2] = 0; // B
        maskData.data[i + 3] = 0; // A - transparent (no tiles)
      }
      
      // Only mark transparent pixels (alpha < threshold) as visible in mask
      const areaX = Math.max(0, Math.floor(area.x));
      const areaY = Math.max(0, Math.floor(area.y));
      const areaWidth = Math.min(Math.floor(area.width), ctx.canvas.width - areaX);
      const areaHeight = Math.min(Math.floor(area.height), ctx.canvas.height - areaY);
      
      for (let y = 0; y < ctx.canvas.height; y++) {
        for (let x = 0; x < ctx.canvas.width; x++) {
          const idx = y * ctx.canvas.width + x;
          const pixelIdx = idx * 4;
          
          if (pixelIdx + 3 < originalImageData.data.length) {
            const alpha = originalImageData.data[pixelIdx + 3];
            
            // Only within the area bounds, check if pixel is transparent
            if (x >= areaX && x < areaX + areaWidth && y >= areaY && y < areaY + areaHeight) {
              // Only if pixel is truly transparent (alpha < threshold), make mask visible
              // This allows tiles to show through ONLY on transparent pixels
              if (alpha < alphaThreshold) {
                maskData.data[pixelIdx] = 255;     // R
                maskData.data[pixelIdx + 1] = 255; // G
                maskData.data[pixelIdx + 2] = 255; // B
                maskData.data[pixelIdx + 3] = 255; // A - visible (tiles will show)
              }
              // If pixel is opaque (alpha >= threshold), mask stays transparent (no tiles)
            }
            // Outside area bounds - mask stays transparent (already initialized)
          }
        }
      }
      
      maskCtx.putImageData(maskData, 0, 0);
      
      // Apply floor view perspective transformation if enabled
      if (floorView && area.type === 'rect') {
        // For floor view, extract tiles BEFORE masking, apply perspective, then mask
        const areaX = Math.floor(area.x);
        const areaY = Math.floor(area.y);
        const areaWidth = Math.floor(area.width);
        const areaHeight = Math.floor(area.height);
        
        // Extract the tiles area from tilesLayer BEFORE masking
        const sourceCanvas = document.createElement('canvas');
        sourceCanvas.width = areaWidth;
        sourceCanvas.height = areaHeight;
        const sourceCtx = sourceCanvas.getContext('2d');
        sourceCtx.drawImage(
          tilesLayer,
          areaX, areaY, areaWidth, areaHeight,
          0, 0, areaWidth, areaHeight
        );
        
        ctx.save();
        
        // Create floor canvas same size as main canvas
        const floorCanvas = document.createElement('canvas');
        floorCanvas.width = ctx.canvas.width;
        floorCanvas.height = ctx.canvas.height;
        const floorCtx = floorCanvas.getContext('2d');
        
        // Apply perspective: scale horizontally more at bottom (simulates looking down)
        const perspectiveFactor = 0.1; // 10% expansion at bottom (subtle effect)
        
        // Draw with perspective using smooth chunks
        const chunkHeight = Math.max(5, Math.floor(areaHeight / 100));
        
        for (let y = 0; y < areaHeight; y += chunkHeight) {
          const progress = y / areaHeight; // 0 at top, 1 at bottom
          const nextProgress = Math.min(1, (y + chunkHeight) / areaHeight);
          
          // Calculate scale for this chunk (wider at bottom)
          const topScale = 1 + (perspectiveFactor * progress);
          const bottomScale = 1 + (perspectiveFactor * nextProgress);
          const avgScale = (topScale + bottomScale) / 2;
          
          const scaledWidth = areaWidth * avgScale;
          const offsetX = (scaledWidth - areaWidth) / 2;
          const chunkH = Math.min(chunkHeight, areaHeight - y);
          
          // Draw chunk with full perspective transformation
          const drawX = areaX - offsetX;
          const drawY = areaY + y;
          
          // Calculate bounds checking
          const sourceX = Math.max(0, -drawX / avgScale);
          const sourceY = y;
          const sourceW = Math.min(areaWidth - sourceX, (ctx.canvas.width - Math.max(0, drawX)) / avgScale);
          const sourceH = chunkH;
          
          const destX = Math.max(0, drawX);
          const destY = Math.max(0, drawY);
          const destW = Math.min(scaledWidth, ctx.canvas.width - destX);
          const destH = Math.min(chunkH, ctx.canvas.height - destY);
          
          // Adjust source if destination was clipped
          const adjustedSourceX = sourceX + ((destX - drawX) / avgScale);
          const adjustedSourceW = destW / avgScale;
          
          if (adjustedSourceW > 0 && sourceH > 0 && destW > 0 && destH > 0) {
            floorCtx.drawImage(
              sourceCanvas,
              adjustedSourceX, sourceY, adjustedSourceW, sourceH,
              destX, destY, destW, destH
            );
          }
        }
        
        // Apply mask to ensure only transparent areas show tiles (same as normal view)
        floorCtx.globalCompositeOperation = 'destination-in';
        floorCtx.drawImage(maskCanvas, 0, 0);
        floorCtx.globalCompositeOperation = 'source-over';
        
        // Draw the transformed floor view onto main canvas
        ctx.drawImage(floorCanvas, 0, 0);
        
        ctx.restore();
      } else {
        // Normal view - apply mask to tiles layer, then draw
        tilesLayerCtx.globalCompositeOperation = 'destination-in';
        tilesLayerCtx.drawImage(maskCanvas, 0, 0);
        tilesLayerCtx.globalCompositeOperation = 'source-over';
        
        // Draw the tiles layer
        ctx.drawImage(tilesLayer, 0, 0);
      }
      
      // Restore context
      ctx.restore();
      
      // Call completion callback
      if (onComplete) {
        onComplete();
      }
    };
    img.src = tileImage;
  };

  const drawSelectionArea = (ctx, area, fillColor) => {
    ctx.save();
    ctx.beginPath();
    
    if (area.type === 'rect') {
      ctx.rect(area.x, area.y, area.width, area.height);
    } else if (area.type === 'polygon') {
      ctx.moveTo(area.points[0].x, area.points[0].y);
      for (let i = 1; i < area.points.length; i++) {
        ctx.lineTo(area.points[i].x, area.points[i].y);
      }
      ctx.closePath();
    }
    
    ctx.fillStyle = fillColor;
    ctx.fill();
    ctx.restore();
  };

  const handleRoomImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setRoomImage(event.target.result);
        setDetectedAreas([]);
        hasAutoDetectedRef.current = false; // Reset detection flag when room image changes
      };
      reader.readAsDataURL(file);
    }
  };

  const getProductData = () => {
    axios.post(API_URL.PRODUCT_WITH_SUB_LIST, {}).then((res) => {
      if (res.data.status === 1) {
        let cd = res.data.data.map((caw) => {
          return {
            label: caw.name,
            value: caw.id,
            image: caw.image,
          };
        });
        setProductDrpData(cd);
      }
    }).catch((error) => {
      console.error('Error fetching products:', error);
      toast.error('Failed to load products', { autoClose: 3000 });
    });
  };

  const handleTileSelect = (selectedOption) => {
    setSelectedProduct(selectedOption);
    if (selectedOption && selectedOption.image) {
      // Convert image URL to data URL or use directly
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
        const dataURL = canvas.toDataURL('image/png');
        setTile(dataURL);
        hasAutoDetectedRef.current = false; // Reset detection flag when tile changes
      };
      img.onerror = () => {
        // If CORS fails, try using the URL directly
        setTile(selectedOption.image);
        hasAutoDetectedRef.current = false;
      };
      img.src = selectedOption.image;
    } else {
      setTile(null);
    }
  };


  const clearAll = () => {
    setDetectedAreas([]);
  };

  const downloadImage = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const link = document.createElement('a');
    link.download = 'tile-visualization.png';
    link.href = canvas.toDataURL();
    link.click();
  };

  // Automatic tile detection - detect transparent areas
  const detectTiles = async () => {
    if (!roomImage) {
      toast.error('Please upload a room image first', { autoClose: 3000 });
      return;
    }

    setDetecting(true);
    setLoading(true);

    try {
      await detectTransparentAreas();
    } catch (error) {
      console.error('Tile detection error:', error);
      toast.error('Error detecting transparent areas. Please try manual selection.', { autoClose: 3000 });
    } finally {
      setDetecting(false);
      setLoading(false);
    }
  };

  // Detect transparent areas from the room image
  const detectTransparentAreas = async () => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous'; // Allow cross-origin images
      img.onload = () => {
        try {
          // Create a working canvas
          const workCanvas = document.createElement('canvas');
          const workCtx = workCanvas.getContext('2d', { willReadFrequently: true });
          workCanvas.width = img.width;
          workCanvas.height = img.height;

          // Clear canvas with transparent background
          workCtx.clearRect(0, 0, workCanvas.width, workCanvas.height);
          
          // Draw the image (this preserves alpha channel for PNG images)
          workCtx.drawImage(img, 0, 0);

          // Get image data with alpha channel
          const imageData = workCtx.getImageData(0, 0, workCanvas.width, workCanvas.height);
          const data = imageData.data;
          
          console.log('Image dimensions:', workCanvas.width, 'x', workCanvas.height);
          console.log('Image data length:', data.length);

          // Find transparent or semi-transparent areas
          const transparentRegions = findTransparentRegions(data, workCanvas.width, workCanvas.height);

          console.log('Total transparent regions found:', transparentRegions.length);

          // Convert all transparent regions to detected areas (no wall/floor classification)
          const allDetectedAreas = [];

          transparentRegions.forEach(region => {
            const detectedArea = {
              type: 'rect',
              x: Math.max(0, region.x),
              y: Math.max(0, region.y),
              width: Math.min(region.width, workCanvas.width - region.x),
              height: Math.min(region.height, workCanvas.height - region.y)
            };

            // Validate area
            if (detectedArea.width > 0 && detectedArea.height > 0) {
              allDetectedAreas.push(detectedArea);
            }
          });
          
          console.log('Detected transparent areas:', allDetectedAreas.length);

          // Merge overlapping areas
          const mergedAreas = mergeOverlappingAreas(allDetectedAreas);

          setDetectedAreas(mergedAreas);

          // If no transparent regions found, preserve original image
          if (mergedAreas.length === 0) {
            toast.info(
              'No transparent areas detected. Original image preserved.',
              { autoClose: 3000 }
            );
          } else {
            toast.success(
              `Detected ${mergedAreas.length} transparent area(s)`,
              { autoClose: 3000 }
            );
          }

          resolve();
        } catch (error) {
          reject(error);
        }
      };
      img.src = roomImage;
    });
  };

  // Find transparent regions in the image - ONLY true transparency, NO white backgrounds
  const findTransparentRegions = (imageData, width, height) => {
    const regions = [];
    const visited = new Set();
    const alphaThreshold = 250; // Consider transparent if alpha < 250
    const minRegionSize = 50; // Minimum pixels in a region

    // Scan for transparent pixels ONLY (sample every 2 pixels for performance)
    // NO fallback to white/light areas - only detect true transparency
    for (let y = 0; y < height; y += 2) {
      for (let x = 0; x < width; x += 2) {
        const idx = y * width + x;
        const pixelIdx = idx * 4;
        
        if (pixelIdx + 3 < imageData.length) {
          const alpha = imageData[pixelIdx + 3]; // Alpha channel
          
          // ONLY detect true transparency - ignore white/colored pixels completely
          // Check if pixel is transparent or semi-transparent (alpha < threshold)
          if (alpha < alphaThreshold && !visited.has(idx)) {
            // Flood fill to find the entire transparent region
            const region = floodFillTransparent(
              imageData, 
              width, 
              height, 
              x, 
              y, 
              visited, 
              alphaThreshold
            );

            // Only add regions that are large enough
            if (region.area >= minRegionSize) {
              regions.push({
                x: region.minX,
                y: region.minY,
                width: region.maxX - region.minX,
                height: region.maxY - region.minY,
                area: region.area
              });
            }
          }
        }
      }
    }

    console.log(`Found ${regions.length} transparent regions (white backgrounds ignored)`);
    return regions;
  };

  // Flood fill algorithm to find connected transparent pixels
  const floodFillTransparent = (imageData, width, height, startX, startY, visited, alphaThreshold) => {
    const stack = [[startX, startY]];
    let minX = startX, maxX = startX, minY = startY, maxY = startY;
    let area = 0;

    while (stack.length > 0) {
      const [x, y] = stack.pop();
      const idx = y * width + x;

      // Check bounds
      if (x < 0 || x >= width || y < 0 || y >= height || visited.has(idx)) {
        continue;
      }

      // Check if pixel is transparent
      const pixelIdx = idx * 4;
      if (pixelIdx + 3 >= imageData.length) continue;
      
      const alpha = imageData[pixelIdx + 3];
      if (alpha >= alphaThreshold) {
        continue; // Not transparent enough
      }

      // Mark as visited and add to region
      visited.add(idx);
      area++;
      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x);
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y);

      // Add neighbors to stack
      stack.push([x + 1, y]);
      stack.push([x - 1, y]);
      stack.push([x, y + 1]);
      stack.push([x, y - 1]);
    }

    return { minX, maxX, minY, maxY, area };
  };

  const isOverlapping = (area1, area2) => {
    const overlapX = area1.x < area2.x + area2.width && area1.x + area1.width > area2.x;
    const overlapY = area1.y < area2.y + area2.height && area1.y + area1.height > area2.y;
    return overlapX && overlapY;
  };

  const mergeOverlappingAreas = (areas) => {
    if (areas.length === 0) return [];

    const merged = [];
    const used = new Set();

    for (let i = 0; i < areas.length; i++) {
      if (used.has(i)) continue;

      let current = { ...areas[i] };
      used.add(i);

      // Find overlapping areas
      for (let j = i + 1; j < areas.length; j++) {
        if (used.has(j)) continue;

        const other = areas[j];
        if (isOverlapping(current, other)) {
          // Merge areas
          current = {
            type: 'rect',
            x: Math.min(current.x, other.x),
            y: Math.min(current.y, other.y),
            width: Math.max(current.x + current.width, other.x + other.width) - Math.min(current.x, other.x),
            height: Math.max(current.y + current.height, other.y + other.height) - Math.min(current.y, other.y)
          };
          used.add(j);
        }
      }

      merged.push(current);
    }

    return merged;
  };

  return (
    <Container fluid>
              <Row>
                {/* Upload Section */}
                <Col md={3}>
                  <Card className="mb-3">
                    <Card.Header>Upload Images</Card.Header>
                    <Card.Body>
                      <div className="mb-3">
                        <label className="form-label">Room Image</label>
                        <input
                          type="file"
                          accept="image/*"
                          className="form-control"
                          onChange={handleRoomImageUpload}
                          ref={roomImageRef}
                        />
                        {roomImage && (
                          <small className="text-success d-block mt-1">✓ Room image loaded</small>
                        )}
                      </div>
                      
                      <div className="mb-3">
                        <label className="form-label">Tile Image</label>
                        <Select
                          options={productDrpData}
                          value={selectedProduct}
                          onChange={handleTileSelect}
                          placeholder="Select a product tile"
                          isClearable
                          styles={{
                            menu: (baseStyles) => ({
                              ...baseStyles,
                              zIndex: 999,
                            }),
                          }}
                        />
                        {tile && (
                          <small className="text-success d-block mt-1">✓ Tile loaded</small>
                        )}
                      </div>
                    </Card.Body>
                  </Card>

                  {/* Controls */}
                  <Card className="mb-3">
                    <Card.Header>Controls</Card.Header>
                    <Card.Body>
                      <div className="mb-3">
                        <label for="tileScale" className="form-label">Scale: {tileScale.toFixed(2)}x</label>
                        <input
                          type="range"
                          className="form-range form-control"
                          min="0.1"
                          max="3"
                          step="0.1"
                          id="tileScale"
                          value={tileScale}
                          onChange={(e) => setTileScale(parseFloat(e.target.value))}
                        />
                      </div>

                      <div className="mb-3">
                        <label for="tileOrientation" className="form-label">Tile Orientation</label>
                        <ButtonGroup className="w-100" aria-label="Tile orientation">
                          <Button
                            variant={tileOrientation === 'horizontal' ? 'primary' : 'outline-primary'}
                            onClick={() => setTileOrientation('horizontal')}
                          >
                            Horizontal
                          </Button>
                          <Button
                            variant={tileOrientation === 'vertical' ? 'primary' : 'outline-primary'}
                            onClick={() => setTileOrientation('vertical')}
                          >
                            Vertical
                          </Button>
                        </ButtonGroup>
                      </div>

                      <div className="mb-3">
                        <label className="form-label">Flip Tiles</label>
                        <div className="d-flex gap-2">
                          <Button
                            variant={flipHorizontal ? 'primary' : 'outline-primary'}
                            onClick={() => setFlipHorizontal(!flipHorizontal)}
                            className="flex-fill"
                          >
                            Horizontal
                          </Button>
                          <Button
                            variant={flipVertical ? 'primary' : 'outline-primary'}
                            onClick={() => setFlipVertical(!flipVertical)}
                            className="flex-fill"
                          >
                            Vertical
                          </Button>
                        </div>
                      </div>

                      <div className="mb-3">
                        <label for="groutWidth" className="form-label">Grout: {groutWidth}&nbsp;</label>
                        <input
                          type="range"
                          id="groutWidth"
                          className="form-range form-control"
                          min="0"
                          max="20"
                          step="1"
                          value={groutWidth}
                          onChange={(e) => setGroutWidth(parseInt(e.target.value))}
                        />
                      </div>

                      <div className="mb-3">
                        <label for="groutColor" className="form-label">Grout Color</label>
                        <div className="d-flex gap-2 align-items-center">
                          <input
                            type="color"
                            id="groutColor"
                            value={groutColor}
                            onChange={(e) => setGroutColor(e.target.value)}
                            style={{
                              width: '60px',
                              height: '38px',
                              border: '1px solid #ced4da',
                              borderRadius: '4px',
                              cursor: 'pointer'
                            }}
                          />
                          <input
                            type="text"
                            className="form-control"
                            value={groutColor}
                            onChange={(e) => setGroutColor(e.target.value)}
                            placeholder="#ffffff"
                            style={{ flex: 1 }}
                          />
                        </div>
                      </div>

                      <div className="mb-3">
                        <label for="tileLayout" className="form-label">Tile Layout</label>
                        <Select
                          id="tileLayout"
                          value={{ value: tileLayout, label: getLayoutLabel(tileLayout) }}
                          onChange={(option) => setTileLayout(option.value)}
                          options={[
                            { value: 'straight', label: 'Straight Stack/Grid' },
                            { value: 'running-bond', label: 'Running Bond (Brick Pattern)' },
                            { value: 'diamond', label: 'Diamond' },
                            { value: 'basketweave', label: 'Basketweave' },
                          ]}
                          styles={{
                            menu: (baseStyles) => ({
                              ...baseStyles,
                              zIndex: 999,
                            }),
                          }}
                        />
                      </div>

                      <div className="mb-3">
                        <label className="form-label">View Mode</label>
                        <ButtonGroup className="w-100" aria-label="View mode">
                          <Button
                            variant={!floorView ? 'primary' : 'outline-primary'}
                            onClick={() => setFloorView(false)}
                            className="flex-fill"
                          >
                            Normal View
                          </Button>
                          <Button
                            variant={floorView ? 'primary' : 'outline-primary'}
                            onClick={() => setFloorView(true)}
                            className="flex-fill"
                          >
                            Floor View
                          </Button>
                        </ButtonGroup>
                      </div>

                      <div className="d-grid gap-2">
                        {detecting && (
                          <div className="text-center mb-2 p-2 border rounded">
                            <Watch color={"#0d6efd"} height={20} width={20} />
                            <small className="d-block mt-1 text-muted">Detecting transparent areas...</small>
                          </div>
                        )}
                        {applying && !detecting && (
                          <div className="text-center mb-2 p-2 border rounded">
                            <Watch color={"#198754"} height={20} width={20} />
                            <small className="d-block mt-1 text-muted">Applying tiles...</small>
                          </div>
                        )}
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={clearAll}
                          disabled={detectedAreas.length === 0}
                        >
                          Clear All Areas ({detectedAreas.length})
                        </Button>
                        <Button
                          variant="success"
                          size="sm"
                          onClick={downloadImage}
                          disabled={!roomImage}
                        >
                          Download Image
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>

                {/* Canvas Section */}
                <Col md={9}>
                  <Card>
                    <Card.Header>
                      <div className="d-flex justify-content-between align-items-center">
                        <span>Canvas Preview</span>
                        {roomImage && (
                          <small className="text-muted">
                            {detectedAreas.length} transparent area(s) detected
                          </small>
                        )}
                      </div>
                    </Card.Header>
                    <Card.Body>
                      <div
                        style={{
                          border: '2px solid #ddd',
                          borderRadius: '4px',
                          overflow: 'auto',
                          maxHeight: '80vh',
                          backgroundColor: '#f5f5f5',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center'
                        }}
                      >
                        {roomImage ? (
                          <canvas
                            ref={canvasRef}
                            style={{
                              cursor: 'default',
                              maxWidth: '100%',
                              height: 'auto'
                            }}
                          />
                        ) : (
                          <div className="text-center p-5 text-muted">
                            <p>Please upload a room image to get started</p>
                          </div>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
    </Container>
  );
};

export default TileVisualizer;